import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ViewerConfig, ViewerState } from "~/type";
import { isBrowser } from "~/utils/envirement";
import { useIsMobile } from "./use-mobile";

const DEFAULT_CONFIG: ViewerConfig = {
  minZoom: 0.8,
  maxZoom: 60,
  springStiffness: 7,
  springAnimationTime: 1.2,
  zoomStep: 1.25,
};

export const useDeepZoomViewer = (config: Partial<ViewerConfig> = {}) => {
  const finalConfig = useMemo(
    () => ({ ...DEFAULT_CONFIG, ...config }),
    [config.minZoom, config.maxZoom, config.springStiffness, config.springAnimationTime, config.zoomStep],
  );
  const viewerRef = useRef<any | null>(null);
  const openSeadragonRef = useRef<any>(null);
  const [isReady, setIsReady] = useState(false);

  const isMobile = useIsMobile();

  // Load OpenSeadragon on client side
  useEffect(() => {
    if (typeof window !== "undefined") {
      import("openseadragon").then((module) => {
        openSeadragonRef.current = module.default;
        setIsReady(true);
      });
    }
  }, []);

  const [state, setState] = useState<ViewerState>({
    zoom: 1,
    positionX: 0,
    positionY: 0,
    isLoaded: false,
    error: null,
  });

  const initializeViewer = useCallback(
    (containerId: string, dziUrl: string) => {
      try {
        if (!openSeadragonRef.current || !isReady) {
          console.error("OpenSeadragon not ready yet");
          return;
        }

        // Ensure absolute path
        let finalUrl = dziUrl;
        // if (!dziUrl.startsWith("/")) {
        //   finalUrl = "/" + dziUrl;
        // }

        viewerRef.current = openSeadragonRef.current({
          id: containerId,
          prefixUrl: "https://cdn.jsdelivr.net/npm/openseadragon@4.1.1/build/openseadragon/images/",
          tileSources: finalUrl,
          showNavigator: true,
          navigatorPosition: isMobile ? "TOP_RIGHT" : "BOTTOM_RIGHT",
          navigatorHeight: isMobile ? 80 : 150,
          navigatorWidth: isMobile ? 80 : 150,
          defaultZoomLevel: 0,
          minZoomLevel: finalConfig.minZoom,
          maxZoomLevel: finalConfig.maxZoom,
          maxZoomPixelRatio: 5,
          constrainDuringPan: true,
          visibilityRatio: 0.5,
          springStiffness: finalConfig.springStiffness,
          animationTime: finalConfig.springAnimationTime,
          autoHideControls: true,
          showZoomControl: false,
          showHomeControl: false,
          showFullPageControl: false,
          sequenceMode: false,
          initialPage: 0,
          navPrevNextWrap: false,
        });

        viewerRef.current.addHandler("open", () => {
          setState((prev) => ({
            ...prev,
            isLoaded: true,
            error: null,
          }));
          updateZoomLevel();
          updatePosition();
        });

        viewerRef.current.addHandler("zoom", updateZoomLevel);
        viewerRef.current.addHandler("pan", updatePosition);

        viewerRef.current.addHandler("open-failed", (event: any) => {
          console.error("✗ Failed to load image:", event);
          setState((prev) => ({
            ...prev,
            error: "Failed to load image",
            isLoaded: false,
          }));
        });
      } catch (error) {
        setState((prev) => ({
          ...prev,
          error: "Failed to initialize viewer",
        }));
      }
    },
    [finalConfig.minZoom, finalConfig.maxZoom, finalConfig.springStiffness, finalConfig.springAnimationTime, finalConfig.zoomStep, isReady],
  );

  const updateZoomLevel = useCallback(() => {
    if (viewerRef.current) {
      const zoom = viewerRef.current.viewport.getZoom(true);
      setState((prev) => ({ ...prev, zoom }));
    }
  }, []);

  const updatePosition = useCallback(() => {
    if (viewerRef.current) {
      const point = viewerRef.current.viewport.getCenter(true);
      setState((prev) => ({
        ...prev,
        positionX: point.x,
        positionY: point.y,
      }));
    }
  }, []);

  const zoomIn = useCallback(() => {
    if (viewerRef.current) {
      viewerRef.current.viewport.zoomBy(finalConfig.zoomStep, null, true);
    }
  }, [finalConfig.zoomStep]);

  const zoomOut = useCallback(() => {
    if (viewerRef.current) {
      viewerRef.current.viewport.zoomBy(1 / finalConfig.zoomStep, null, true);
    }
  }, [finalConfig.zoomStep]);

  const zoomTo = useCallback(
    (level: number) => {
      if (viewerRef.current) {
        const clampedLevel = Math.max(finalConfig.minZoom, Math.min(finalConfig.maxZoom, level));
        viewerRef.current.viewport.zoomTo(clampedLevel, null, true);
      }
    },
    [finalConfig.minZoom, finalConfig.maxZoom],
  );

  const fitBounds = useCallback(() => {
    if (viewerRef.current) {
      const item = viewerRef.current.world.getItemAt(0);
      if (item) {
        viewerRef.current.viewport.fitBounds(item.getBounds(), true);
      }
    }
  }, []);

  const reset = useCallback(() => {
    if (viewerRef.current) {
      viewerRef.current.viewport.goHome(true);
    }
  }, []);

  const requestFullscreen = useCallback((element: HTMLElement) => {
    if (!isBrowser()) return;

    if (document.fullscreenElement) {
      document.exitFullscreen().catch(() => {});
    } else {
      element.requestFullscreen().catch(() => {});
    }
  }, []);

  return {
    viewerRef,
    state,
    initializeViewer,
    zoomIn,
    zoomOut,
    zoomTo,
    fitBounds,
    reset,
    requestFullscreen,
  };
};
